import { CURRICULUM_SLUGS, DEFAULT_RETRY_STRATEGY } from '@/constants'
import type {
	AceGroup,
	ReleasenoteAceKla,
	ReleasenoteAceSyllabus,
	ReleasenoteGeneral,
	ReleasenoteSyllabus,
	ReleasenoteSyllabusKla,
	ReleasenoteSyllabusMultiple,
	ResourceContainer,
	UiMenu,
	WebLinkSyllabus,
	Weblinkint,
} from '@/kontent/content-types'
import type { Syllabus } from '@/kontent/content-types/syllabus'
import type { WpHomepage } from '@/kontent/content-types/wp_homepage'
import { contentTypes } from '@/kontent/project/contentTypes'
import type {
	IAssetResponseFromMAPI,
	Mapping,
	MappingParams,
} from '@/types/index'
import { buildAceReleaseNotesMappings } from '@/utils/ace/buildAceReleaseNotesMappings'
import { buildAceUrlMappings } from '@/utils/ace/buildAceUrlMappings'
import { getMappingByCodename } from '@/utils/getUrlFromMapping'
import {
	filterPreviewableSyllabusesOnly,
	getSlugByCodename,
	isAllowPreviewExternalSyllabus,
} from '@/utils/index'
import {
	DeliveryError,
	Elements,
	IContentItem,
	MultipleItemsQuery,
	Responses,
	SingleItemQuery,
	SortOrder,
} from '@kontent-ai/delivery-sdk'
import { DeliveryClient } from '@kontent-ai/delivery-sdk/dist/cjs/client/delivery-client'

import { DEFAULT_EXCLUDED_FIELDS_FROM_MAPPINGS } from '@/constants'
import { FilterParams, IGetItemByCodenameParams } from '@/types'
import { optimiseSystemJson } from '@/utils/optimise-json'
import { getSyllabusTitleWithPublicationYear } from '@/utils/syllabus/getSyllabusTitleWithPublicationYear'
import type { SharedModels, TaxonomyModels } from '@kontent-ai/management-sdk'
import { createManagementClient } from '@kontent-ai/management-sdk/dist/cjs/client/index'
import slugify from 'slugify'
import SITEMAP_JSON from '../kontent/sitemap-mappings.json'
import WEBSITE_CONFIG_JSON from '../kontent/website-config.json'
import { client } from './client'
import { fnReturnData } from './fnReturnData'
import { getAllItemsByType } from './getAllItemsByType'

const managementClient = createManagementClient({
	projectId: process.env.NEXT_PUBLIC_KONTENT_PROJECT_ID,
	apiKey: process.env.KONTENT_MANAGEMENT_API_KEY,
	retryStrategy: DEFAULT_RETRY_STRATEGY,
})

let SITE_CONFIG = {
	preview: null,
	published: undefined,
}
let SITE_MAPPINGS = {
	preview: null,
	published: undefined,
}
export async function getCachedAssets(preview = false) {
	return getAllItemsByType<ResourceContainer>({
		type: contentTypes.resource_container.codename,
		preview,
	})
}

export async function getAllAssets(): Promise<IAssetResponseFromMAPI> {
	return await managementClient.listAssets().toAllPromise().then(fnReturnData)
}

export async function getAllTaxonomies(): Promise<{
	items: TaxonomyModels.Taxonomy[]
	pagination: SharedModels.Pagination
}> {
	return await managementClient
		.listTaxonomies()
		.toAllPromise()
		.then(fnReturnData)
}

export async function loadWebsiteConfig(
	preview = false,
	noCache = false,
): Promise<Responses.IViewContentItemResponse<WpHomepage>> {
	const key = preview ? 'preview' : 'published'
	if (preview || noCache) {
		if (SITE_CONFIG[key]) {
			return SITE_CONFIG[key]
		}

		const result = await getItemByCodename<WpHomepage>({
			codename: 'homepage',
			depth: 5,
			elementsParameter: [
				// Page tab
				contentTypes.wp_homepage.elements.title.codename,
				contentTypes.wp_homepage.elements.subtitle.codename,

				// Site config — moved to website_configuration, commented out pending cleanup
				// contentTypes.wp_homepage.elements.site_prefix.codename,
				// contentTypes.wp_homepage.elements.favicon.codename,
				// contentTypes.wp_homepage.elements.descriptor.codename,
				// CMS fields marked for deletion (renamed to del_*) - commented out pending cleanup
				// contentTypes.wp_homepage.elements.palette.codename,
				// contentTypes.wp_homepage.elements.disabled_stages.codename,
				// contentTypes.wp_homepage.elements.disabled_key_learning_areas
				// 	.codename,

				contentTypes.contentrichtext.elements.content.codename,

				// Site config - global alert and global info
				contentTypes.wp_homepage.elements.global_info.codename,
				contentTypes.wp_homepage.elements.global_alert_ace.codename,
				contentTypes.ui_globalalert.elements.intro.codename,
				contentTypes.ui_globalalert.elements.content.codename,
				contentTypes.ui_globalalert.elements.btn_primary_text.codename,
				contentTypes.ui_globalalert.elements.btn_primary_url.codename,
				contentTypes.ui_globalalert.elements.btn_secondary_text
					.codename,
				contentTypes.ui_globalalert.elements.btn_secondary_url.codename,
				contentTypes.ui_globalalert.elements.type.codename,

				// Header tab items — logo_main moved to website_configuration
				// contentTypes.wp_homepage.elements.logo_main.codename,
				contentTypes.wp_homepage.elements.main_menu.codename,

				// Header/Footer - main menu (collection, e.g. Main Menu links - Footer menu links)
				contentTypes.collection_weblink.elements.items.codename,

				// Header / Footer - main menu - children
				contentTypes.ui_menu.elements.item.codename,
				contentTypes.ui_menu.elements.subitems.codename,
				contentTypes.ui_menu.elements.subtitle.codename,
				contentTypes.ui_menu.elements.title.codename,
				contentTypes.ui_menu.elements.titlelong.codename,
				contentTypes.weblinkint.elements.title.codename,
				contentTypes.weblinkint.elements.anchorlink.codename,
				contentTypes.weblinkint.elements.item.codename,
				contentTypes.weblinkint.elements.subtitle.codename,
				contentTypes.weblinkext.elements.title.codename,
				contentTypes.weblinkext.elements.subtitle.codename,
				contentTypes.weblinkext.elements.link_url.codename,

				// Depth 4 because of this
				// Header tab items - main menu children - item - slug
				contentTypes.web_page.elements.slug.codename,

				// Depth 5
				contentTypes.wp_stage.elements.stages__stages.codename,

				// Footer tab elements — moved to website_configuration, commented out pending cleanup
				// contentTypes.wp_homepage.elements.footer_top_content.codename,
				// contentTypes.wp_homepage.elements.social_links.codename,
				// contentTypes.wp_homepage.elements.footer_menu_links.codename,
				// contentTypes.wp_homepage.elements.acknowledgement.codename,
				// contentTypes.wp_homepage.elements.secondary_links.codename,
				// contentTypes.wp_homepage.elements.copyright_link.codename,

				//Tooltips
				// CMS fields marked for deletion (renamed to del_*) - commented out pending cleanup
				// contentTypes.wp_homepage.elements
				// 	.syllabusoutcomes_alignedcontent.codename,
				// contentTypes.wp_homepage.elements
				// 	.syllabusoutcomes_relatedoutomes.codename,
				// contentTypes.wp_homepage.elements
				// 	.syllabussenioroutcomes_relatedoutomes.codename,
				// contentTypes.wp_homepage.elements
				// 	.syllabussenioroutcomes_relatedoutomes_ls.codename,

				// Message
				// CMS fields marked for deletion (renamed to del_*) - commented out pending cleanup
				// contentTypes.wp_homepage.elements.glossary_intro.codename, // glossary intro
				// contentTypes.wp_homepage.elements.nocontent_teachingadvice
				// 	.codename,
				// contentTypes.wp_homepage.elements.externallink_popup_message
				// 	.codename,
				// contentTypes.wp_homepage.elements.overarchingoutcome_message
				// 	.codename,
			],
			preview,
		})
		SITE_CONFIG[key] = result
		return result
	}

	return WEBSITE_CONFIG_JSON as unknown as Responses.IViewContentItemResponse<WpHomepage>
}

async function getSubPaths(data, pagesCodenames, parentSlug, preview = false) {
	const paths = []

	for (const pageCodename of pagesCodenames) {
		const currentItem = data.linkedItems[pageCodename]
		if (!currentItem || (currentItem && !currentItem.elements?.slug)) {
			continue
		}

		const pageSlug = parentSlug.concat(currentItem.elements.slug.value)

		paths.push({
			params: {
				pageTitle: currentItem.elements.title?.value || '',
				slug: pageSlug,
				navigationItem: optimiseSystemJson(
					currentItem.system,
					DEFAULT_EXCLUDED_FIELDS_FROM_MAPPINGS,
				), // will be ignored by next in getContentPaths
				excludeInSitemap: false,
			},
		})

		if (currentItem.elements?.subpages?.value) {
			const subPaths = await getSubPaths(
				data,
				currentItem.elements['subpages'].value,
				pageSlug,
				preview,
			)
			paths.push(...subPaths)
		}
	}

	return paths
}

export async function getAllOtherPotentialLinks(
	mappings: Mapping[],
	preview,
): Promise<Mapping[]> {
	const [weblinkints, weblinkSyllabus, uimenus] = await Promise.all([
		getAllItemsByType<Weblinkint>({
			type: contentTypes.weblinkint.codename,
			preview,
		}),
		getAllItemsByType<WebLinkSyllabus>({
			type: contentTypes.web_link_syllabus.codename,
			preview,
		}),
		getAllItemsByType<UiMenu>({
			type: contentTypes.ui_menu.codename,
			preview,
		}),
	])

	const filterFn = <
		TItem extends { item: Elements.LinkedItemsElement },
		T extends IContentItem<TItem>,
	>(
		item: T,
	) => {
		return item.elements.item.value?.[0]
	}

	const weblinkIntMappings = weblinkints.items
		.filter(filterFn)
		.map((_weblinkint) => {
			return {
				params: {
					slug: [],
					...getMappingByCodename(
						mappings,
						_weblinkint.elements.item.value[0],
					)?.params,
					navigationItem: optimiseSystemJson(
						_weblinkint.system,
						DEFAULT_EXCLUDED_FIELDS_FROM_MAPPINGS,
					),
					additional: `${
						_weblinkint.elements.querystring.value
							? `?${_weblinkint.elements.querystring.value}`
							: ''
					}${
						_weblinkint.elements.anchorlink.value
							? `#${_weblinkint.elements.anchorlink.value}`
							: ''
					}`,
					excludeInSitemap: true,
				},
			} as Mapping
		})

	const weblinkSyllabusMappings = weblinkSyllabus.items
		.filter(filterFn)
		.map((_weblinkSyllabus) => {
			let tab =
				getSlugByCodename(
					_weblinkSyllabus.elements.tab?.value?.[0]?.codename,
				) || 'overview'
			if (tab === 'course-overview') tab = 'overview'

			// by default, the syllabus mapping will have /overview
			const _params =
				getMappingByCodename(
					mappings,
					_weblinkSyllabus.elements.item.value[0],
				)?.params || ({} as MappingParams)

			return {
				params: {
					..._params,
					slug: _params.slug
						? _params.slug.filter((s, index, _slugs) => {
								// remove the last slug if it's overview
								if (index === _slugs.length - 1) {
									return s !== 'overview'
								}
								return true
						  })
						: [],
					navigationItem: optimiseSystemJson(
						_weblinkSyllabus.system,
						DEFAULT_EXCLUDED_FIELDS_FROM_MAPPINGS,
					),
					additional: `/${tab}`,
					excludeInSitemap: true,
				},
			} as Mapping
		})

	// combined them all first so that the uimenus that use the weblinks, can be searched
	const combinedWeblinkMappings = [
		...weblinkIntMappings,
		...weblinkSyllabusMappings,
	]

	return [
		...combinedWeblinkMappings,
		...uimenus.items.filter(filterFn).map((_uiMenu) => {
			return {
				params: {
					slug: [],
					...getMappingByCodename(
						[...combinedWeblinkMappings, ...mappings],
						_uiMenu.elements.item.value[0],
					)?.params,
					navigationItem: optimiseSystemJson(
						_uiMenu.system,
						DEFAULT_EXCLUDED_FIELDS_FROM_MAPPINGS,
					),
					excludeInSitemap: true,
				},
			} as Mapping
		}),
	]
}

export async function getSiteMappings(
	preview = false,
	noCache = false,
	excludedContentTypes = [],
): Promise<Mapping[]> {
	const key = preview ? 'preview' : 'published'

	const fnExcludeContentTypes = (mapping) => {
		if (!excludedContentTypes.length) return true
		return !excludedContentTypes.includes(
			mapping.params.navigationItem.type,
		)
	}

	if (preview || noCache) {
		if (SITE_MAPPINGS[key]) {
			return SITE_MAPPINGS[key]
		}

		try {
			const [data, syllabusMappings, aceGroupMappings] =
				await Promise.all([
					getItemByCodename<WpHomepage>({
						codename: 'homepage',
						depth: 5,
						elementsParameter: [
							'subpages',
							'slug',
							'title',
							'web_content_rtb__content',
						],
						preview,
					}),

					// there's no subpages specified for syllabus, that's why we created the paths
					// based on the syllabus list
					getAllSyllabusMappings(preview),
					getAllAceMappings(preview),
				])

			if (!data || !data.item) {
				// Fallback to cached sitemap if homepage API call fails
				console.error(
					'[ERROR] getSiteMappings: Homepage data not found, falling back to cached sitemap',
				)
				return (SITEMAP_JSON && Array.isArray(SITEMAP_JSON)
					? SITEMAP_JSON.filter(fnExcludeContentTypes)
					: []) as unknown as Mapping[]
			}

			const rootSlug = []
			const pathsFromKontent: Mapping[] = [
				{
					params: {
						pageTitle: data.item.elements.title.value,
						slug: rootSlug,
						navigationItem: optimiseSystemJson(
							data.item.system,
							DEFAULT_EXCLUDED_FIELDS_FROM_MAPPINGS,
						), // will be ignored by next in getContentPaths
						excludeInSitemap: false,
						isCanonical: true,
					},
				},
			]

			const subPaths = await getSubPaths(
				data,
				data.item.elements['subpages'].value,
				rootSlug,
				preview,
			)

			const tmpMappings = pathsFromKontent.concat(
				...subPaths,
				...syllabusMappings,
				...aceGroupMappings,
			)
			const allOtherPaths = await getAllOtherPotentialLinks(
				tmpMappings,
				preview,
			)
			const result = [...tmpMappings, ...allOtherPaths]
			SITE_MAPPINGS[key] = result
			return result.filter(fnExcludeContentTypes)
		} catch (error) {
			console.error('[ERROR] getSiteMappings API calls failed:', error)
			// Fallback to cached sitemap on error
			return (SITEMAP_JSON && Array.isArray(SITEMAP_JSON)
				? SITEMAP_JSON.filter(fnExcludeContentTypes)
				: []) as unknown as Mapping[]
		}
	}
	return (SITEMAP_JSON && Array.isArray(SITEMAP_JSON)
		? SITEMAP_JSON.filter(fnExcludeContentTypes)
		: SITEMAP_JSON) as unknown as Mapping[]
}
/**
 * Kontent error code 6 — "Your request reached the maximum response size of 2001 content
 * items". Raised when `depth` + `elements` resolve too large a linked-item tree.
 * @see https://kontent.ai/learn/dapi-response-size
 */
const KONTENT_ERROR_CODE_RESPONSE_TOO_LARGE = 6

export async function getItemByCodename<T extends IContentItem>({
	codename,
	depth = 1,
	elementsParameter,
	preview,
	kontentClient = client,
}: IGetItemByCodenameParams): Promise<
	Responses.IViewContentItemResponse<T> | undefined
> {
	let temp = kontentClient.item<T>(codename).depthParameter(depth)
	if (elementsParameter) {
		temp = temp.elementsParameter(elementsParameter)
	}
	let response

	try {
		response = await temp
			.queryConfig({ usePreviewMode: preview })
			.toPromise()
		return client.mappingService.viewContentItemResponse<T>(
			response.response.data,
		)
	} catch (error) {
		// not swallow this silently. Returning `undefined` here used to surface several
		// call frames away as an unrelated "Cannot destructure property ... of null",
		// which made (Kontent's 2001-item response cap, error code 6) very hard to diagnose.
		const deliveryError = error as Partial<DeliveryError>
		const errorCodeSuffix =
			deliveryError?.errorCode === KONTENT_ERROR_CODE_RESPONSE_TOO_LARGE
				? ' — Kontent error code 6: the response exceeded the 2001-item cap. Reduce depth, or split the request and merge the results.'
				: deliveryError?.errorCode
				? ` — Kontent error code ${deliveryError.errorCode}`
				: ''

		console.error(
			`[ERROR] getItemByCodename failed for "${codename}" (depth ${depth}, ${
				elementsParameter?.length ?? 'all'
			} elements)${errorCodeSuffix}:`,
			deliveryError?.message ?? error,
		)
		return
	}
}

export async function getItemsFeed<T extends IContentItem>({
	type,
	order = null,
	elementsParameter = null,
	containsFilter = null,
	allFilter = null,
	anyFilter = null,
	equalsFilter = null,
	notEmptyFilter = null,
	notEqualsFilter = null,
	inFilter = null,
	limitParameter = null,
	preview,
	kontentClient = client,
}: {
	type: string
	depth?: number
	order?: { element: string; sortOrder: SortOrder }
	elementsParameter?: string[]
	containsFilter?: FilterParams
	allFilter?: FilterParams
	anyFilter?: FilterParams
	equalsFilter?: { element: string; value: string }
	notEmptyFilter?: { element: string }
	notEqualsFilter?: { element: string; value: string }
	inFilter?: FilterParams
	limitParameter?: number
	preview: boolean
	kontentClient?: DeliveryClient
}): Promise<Responses.IListItemsFeedResponse<T>> {
	let temp = kontentClient.itemsFeed<T>().type(type)

	if (order) {
		temp = temp.orderParameter(order.element, order.sortOrder)
	}
	if (elementsParameter) {
		temp = temp.elementsParameter(elementsParameter)
	}
	if (containsFilter) {
		temp = temp.containsFilter(containsFilter.element, containsFilter.value)
	}
	if (allFilter) {
		temp = temp.allFilter(allFilter.element, allFilter.value)
	}
	if (anyFilter) {
		temp = temp.anyFilter(anyFilter.element, anyFilter.value)
	}
	if (equalsFilter) {
		temp = temp.equalsFilter(equalsFilter.element, equalsFilter.value)
	}
	if (notEmptyFilter) {
		temp = temp.notEmptyFilter(notEmptyFilter.element)
	}
	if (notEqualsFilter) {
		temp = temp.notEqualsFilter(
			notEqualsFilter.element,
			notEqualsFilter.value,
		)
	}
	if (inFilter) {
		temp = temp.inFilter(inFilter.element, inFilter.value)
	}
	if (limitParameter) {
		temp = temp.limitParameter(limitParameter)
	}

	const result = await temp
		.queryConfig({ usePreviewMode: preview })
		.toAllPromise()
		.then(fnReturnData)

	// OC2-5955: toAllPromise() merges items across pages but drops linkedItems.
	// Extract and merge linkedItems from individual page responses.
	result.linkedItems =
		(result as any).responses?.reduce((acc, response) => {
			Object.assign(acc, response.data?.linkedItems)
			return acc
		}, {}) || {}

	return result
}

type TCommonGetItemParams<
	T extends IContentItem,
	TQueryType extends SingleItemQuery<T> | MultipleItemsQuery<T>,
> = {
	preview: boolean
	depth?: number
	elementsParameter?: string[]
	moreQueryFn?: (_query: TQueryType) => TQueryType
	kontentClient?: DeliveryClient
}

export function getItemByCodenameV2<T extends IContentItem>({
	codename,
	depth = 1,
	preview,
	moreQueryFn,
	kontentClient = client,
}: TCommonGetItemParams<T, SingleItemQuery<T>> & {
	codename: string
}): Promise<Responses.IViewContentItemResponse<T>> {
	const firstQuery = kontentClient.item<T>(codename).depthParameter(depth)
	const query = moreQueryFn ? moreQueryFn(firstQuery) : firstQuery

	return query
		.queryConfig({ usePreviewMode: preview })
		.toPromise()
		.then(fnReturnData)
}

export function getAllItemsByTypeV2<T extends IContentItem>({
	type,
	preview,
	depth = 0,
	moreQueryFn,
	elementsParameter,
	kontentClient = client,
}: TCommonGetItemParams<T, MultipleItemsQuery<T>> & {
	type: string
}): Promise<Responses.IListContentItemsResponse<T>> {
	const firstQuery = kontentClient.items<T>().type(type).depthParameter(depth)
	if (elementsParameter) {
		firstQuery.elementsParameter(elementsParameter)
	}
	const query = moreQueryFn ? moreQueryFn(firstQuery) : firstQuery
	return query
		.queryConfig({ usePreviewMode: preview })
		.toPromise()
		.then(fnReturnData)
}

export function getTaxonomy(
	taxonomyGroup: string,
): Promise<Responses.IViewTaxonomyResponse> {
	return client.taxonomy(taxonomyGroup).toPromise().then(fnReturnData)
}

async function getAllSyllabusMappings(preview): Promise<Mapping[]> {
	const ctSyllabus = contentTypes.syllabus
	// This should be filtered by whether it's doing redirect or not
	const data = await getAllItemsByType<Syllabus>({
		type: ctSyllabus.codename,
		depth: 0,
		preview,
		elementsParameter: [
			ctSyllabus.elements.title.codename,
			ctSyllabus.elements.key_learning_area__items.codename,
			ctSyllabus.elements.key_learning_area_default.codename,
			ctSyllabus.elements.syllabus.codename,
			ctSyllabus.elements.doredirect.codename,
			ctSyllabus.elements.allowpreview.codename,
			ctSyllabus.elements.publication_year.codename,
		],
		containsFilter: isAllowPreviewExternalSyllabus()
			? null
			: {
					element: `elements.${ctSyllabus.elements.doredirect.codename}`,
					value: ['no'],
			  },
	})

	const previewableSyllabusesOnly = data.items.filter(
		filterPreviewableSyllabusesOnly,
	)

	return [
		// /learning-areas/[kla-codename]/[syllabus-codename]
		...previewableSyllabusesOnly.flatMap((_syllabus) => {
			const { key_learning_area__items, key_learning_area_default } =
				_syllabus.elements

			const keyLearningAreaDefaultCodename =
				key_learning_area_default?.value?.[0]?.codename ||
				key_learning_area__items?.value?.[0]?.codename

			return key_learning_area__items?.value.map((keyLearningArea) => {
				const pageTitle = getSyllabusTitleWithPublicationYear(
					_syllabus,
					false,
				)

				return {
					params: {
						pageTitle,
						slug: [
							...CURRICULUM_SLUGS.LEARNING_AREAS,
							slugify(
								keyLearningArea.codename.replace(/_/g, '-') ||
									'',
							),
							slugify(
								_syllabus.system.codename.replace(/_/g, '-') ||
									'',
							),
							'overview',
						].filter((item) => !!item),
						navigationItem: optimiseSystemJson(
							_syllabus.system,
							DEFAULT_EXCLUDED_FIELDS_FROM_MAPPINGS,
						),
						excludeInSitemap: false,
						taxoSyllabus:
							_syllabus.elements.syllabus.value?.[0]?.codename,
						isCanonical:
							keyLearningAreaDefaultCodename ===
							keyLearningArea.codename,
						kla_default: keyLearningAreaDefaultCodename,
					},
				} as Mapping
			})
		}),
	]
}
async function getAllAceMappings(preview): Promise<Mapping[]> {
	const ctAceGroup = contentTypes.ace_group
	// This should be filtered by whether it's doing redirect or not
	const [
		aceGroupsForMapping,
		rnSyllabus,
		rnSyllabusKla,
		rnSyllabusMultiple,
		rnAceSyllabus,
		rnAceKla,
		rnGeneral,
	] = await Promise.all([
		getAllItemsByType<AceGroup>({
			type: ctAceGroup.codename,
			depth: 3,
			preview,
		}),
		getAllItemsByType<ReleasenoteSyllabus>({
			type: contentTypes.releasenote_syllabus.codename,
			preview,
		}),
		getAllItemsByType<ReleasenoteSyllabusKla>({
			type: contentTypes.releasenote_syllabus_kla.codename,
			preview,
		}),
		getAllItemsByType<ReleasenoteSyllabusMultiple>({
			type: contentTypes.releasenote_syllabus_multiple.codename,
			preview,
		}),
		getAllItemsByType<ReleasenoteAceSyllabus>({
			type: contentTypes.releasenote_ace_syllabus.codename,
			preview,
		}),
		getAllItemsByType<ReleasenoteAceKla>({
			type: contentTypes.releasenote_ace_kla.codename,
			preview,
		}),
		getAllItemsByType<ReleasenoteGeneral>({
			type: contentTypes.releasenote_general.codename,
			preview,
		}),
	])

	return [
		...buildAceReleaseNotesMappings(rnSyllabus),
		...buildAceReleaseNotesMappings(rnSyllabusKla),
		...buildAceReleaseNotesMappings(rnSyllabusMultiple),
		...buildAceReleaseNotesMappings(rnAceSyllabus),
		...buildAceReleaseNotesMappings(rnAceKla),
		...buildAceReleaseNotesMappings(rnGeneral),
		...buildAceUrlMappings(aceGroupsForMapping),
	]
}
